import React, { useEffect, useState, useCallback, useMemo } from "react";
import EmrVisitsService from "../../../../services/EmrVisitsService";
import { capitalizeName, isSet } from "../../../../utils/commonUtils";
import TableGrid from "../../../../components/UI/TableGrid";
import FileManagerService from "../../../../services/FileManagerService";
import PERMISSIONS from "../../../../utils/AclPermissions";
import Buttons from "../../../../components/UI/Buttons";
import FormInputs from "../../../../components/UI/FormInputs";
import AddPrescriptionModal from "./AddPrescriptionModal";

const PrescribedMedicationsPage = (props) => {
  const { visit_id, Loader, visitDetails, employeeDetails, employeeId, fetchEmployeeDetails } = props;
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewPrescriptionData, setViewPrescriptionData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPrescriptionIds, setSelectedPrescriptionIds] = useState([]);
  const [formData, setFormData] = useState({
    provider_name: "",
    medication_name: "",
    generic_name: "",
    category: "",
    quantity: "",
    medication_form: "",
    sig_options: "",
    refill: "",
    prescribed_date: "",
  });

  const handleCheckboxChange = useCallback((value, { e }, rowId = '') => {
    if (!rowId) {
      rowId = e.target.id;
    }
    
    setTableRecords((prevState) => {
      const { tableRows } = prevState;
      let updatedData;
      let updatedSelectedIds = [];
      
      if (rowId === 'ALL') {
        // Select/deselect all
        const allSelected = tableRows.data.every(item => item.data.isActive === 1);
        
        if (allSelected) {
          // Deselect all
          updatedSelectedIds = [];
          updatedData = tableRows.data.map((item) => {
            item.data.isActive = 0;
            return item;
          });
        } else {
          // Select all
          updatedSelectedIds = tableRows.data.map(item => item.rowId);
          updatedData = tableRows.data.map((item) => {
            item.data.isActive = 1;
            return item;
          });
        }
      } else {
        // Toggle individual checkbox
        const prescriptionId = rowId;
        const currentSelectedIds = selectedPrescriptionIds;
        const isCurrentlySelected = currentSelectedIds.includes(prescriptionId);
        
        updatedSelectedIds = isCurrentlySelected 
          ? currentSelectedIds.filter(id => id !== prescriptionId)
          : [...currentSelectedIds, prescriptionId];
        
        updatedData = tableRows.data.map((item) => {
          if (item.rowId === prescriptionId) {
            item.data.isActive = isCurrentlySelected ? 0 : 1;
          }
          return item;
        });
      }
      
      // Update selected IDs separately (React will batch these updates)
      setSelectedPrescriptionIds(updatedSelectedIds);
      
      // Update "Select All" checkbox state
      setTimeout(() => {
        const allSelected = updatedData.every(item => item.data.isActive === 1);
        const allCheckbox = document.getElementById('ALL');
        if (allCheckbox) {
          allCheckbox.checked = allSelected;
        }
      }, 0);
      
      return {
        ...prevState,
        tableRows: {
          ...prevState.tableRows,
          data: updatedData,
        },
      };
    });
  }, [selectedPrescriptionIds]);

  const tableHeaders = useMemo(() => [
    { label: "", key: "isActive", type: "checkBox", name: "ALL", changeHandler: handleCheckboxChange },
    { label: "Provider Name", key: "provider_name" },
    { label: "Reference Name", key: "prescription_reference" },
    { label: "Refill", key: "refill" },
    { label: "Prescribed Date", key: "prescribed_date" },
    { label: "Actions", key: "action", type: "Actions" },
  ], [handleCheckboxChange]);

  const [tableRecords, setTableRecords] = useState({
    tableHeaders: tableHeaders,
    tableRows: {
      data: [],
      status: 1000,
    },
    pagination: {
      page: 1,
      perPage: 25,
      totalRecords: 0,
      sort_by: "date",
      sort_order: "desc",
      search: {},
    },
  });

  useEffect(() => {
    if (visit_id) {
      fetchPrescribedMedications();
    }
  }, [visit_id]);

  const fetchPrescribedMedications = async () => {
    if (!visit_id) return;

    try {
      Loader(true);
      const response = await EmrVisitsService.getPrescribedMedications(visit_id);
      const data = response || { data: [], pagination: { total: 0 } };

      if (data && !data.error) {
        const prescriptions = data.data || [];
        const tableData = prescriptions.map((prescription) => {
          const isSelected = selectedPrescriptionIds.includes(prescription.prescription_id);
          return {
            rowId: prescription.prescription_id || prescription.id,
            data: {
              isActive: isSelected ? 1 : 0,
              provider_name: {
                value: (
                  <span className="forTable_textEllipsis" title={prescription.provider_name || "-"}>
                    {prescription.provider_name || "-"}
                  </span>
                ),
              },
              prescription_reference: {
                value: (
                  <span className="forTable_textEllipsis" title={prescription.prescription_reference || "-"}>
                    {prescription.prescription_reference || "-"}
                  </span>
                ),
              },
              refill: {
                value: (
                  <span className="forTable_textEllipsis" title={prescription.refill || "-"}>
                    {prescription.refill || "-"}
                  </span>
                ),
              },
              prescribed_date: {
                value: (
                  <span className="forTable_textEllipsis" title={prescription.prescribed_date || "-"}>
                    {prescription.prescribed_date || "-"}
                  </span>
                ),
              },
              action: [
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Search",
                  title: "View",
                  acl: PERMISSIONS.VISITS.EDIT,
                  clickHandler: () => handleView(prescription),
                },
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Remove",
                  title: "Delete",
                  isDelete: true,
                  acl: PERMISSIONS.VISITS.DELETE,
                  clickHandler: () => handleDelete(prescription),
                },
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Print",
                  title: "Print",
                  acl: PERMISSIONS.VISITS.EDIT,
                  clickHandler: () => handlePrint(prescription),
                },
              ],
            },
          };
        });

        setTableRecords((prevState) => ({
          ...prevState,
          tableHeaders: tableHeaders,
          tableRows: {
            data: tableData,
            status: 200,
          },
          pagination: {
            ...prevState.pagination,
            totalRecords: data.pagination?.total || 0,
          },
        }));
      } else {
        console.error("Error fetching prescribed medications:", data?.error || "Unknown error");
        if (props.Toast) {
          props.Toast.error(data?.message || "Failed to fetch prescribed medications");
        }
        setTableRecords((prevState) => ({
          ...prevState,
          tableRows: {
            data: [],
            status: 200,
          },
          pagination: {
            ...prevState.pagination,
            totalRecords: 0,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching prescribed medications:", error);
      if (props.Toast) {
        props.Toast.error("Failed to fetch prescribed medications");
      }
      setTableRecords((prevState) => ({
        ...prevState,
        tableRows: {
          data: [],
          status: 200,
        },
        pagination: {
          ...prevState.pagination,
          totalRecords: 0,
        },
      }));
    } finally {
      Loader(false);
    }
  };

  const handleView = async (prescription) => {
    try {
      if (Loader) Loader(true);
      const response = await EmrVisitsService.getPrescribedMedicationById(visit_id, prescription.prescription_id);
      if (response && !response.error) {
        setViewPrescriptionData(response);
        setIsEditMode(true);
        setIsModalOpen(true);
      } else {
        if (props.Toast) {
          props.Toast.error(response?.error || "Failed to fetch prescription details");
        }
      }
    } catch (error) {
      console.error("Error viewing prescription:", error);
      if (props.Toast) {
        props.Toast.error("Failed to view prescription details");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  const handleDelete = async (prescription) => {
    if (!prescription.prescription_id) {
      if (props.Toast) {
        props.Toast.error("Prescription ID is required");
      }
      return;
    }

    try {
      if (Loader) Loader(true);
      await EmrVisitsService.deletePrescribedMedications(visit_id, prescription.prescription_id);
      if (props.Toast) {
        props.Toast.success("Prescription deleted successfully");
      }
      fetchPrescribedMedications();
    } catch (error) {
      console.error("Error deleting prescription:", error);
      if (props.Toast) {
        props.Toast.error(error.message || "Failed to delete prescription");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  const handlePrint = async (prescription) => {
    try {
      if (Loader) Loader(true);
      
      if (!prescription.prescription_id) {
        if (props.Toast) {
          props.Toast.error("Prescription ID is required");
        }
        return;
      }

      const response = await EmrVisitsService.printPrescribedMedication(visit_id, prescription.prescription_id);
      
      // Handle different response structures
      const base64Data = typeof response === 'string' ? response : (response?.data || response);
      
      if (base64Data && typeof base64Data === 'string' && base64Data.length > 0) {
        const pdfData = {
          file_content: base64Data,
          original_name: `Prescription_${prescription.prescription_reference || prescription.prescription_id}`,
          mime_type: 'application/pdf'
        };
        FileManagerService.printPdf(pdfData);
      } else {
        console.error("Invalid PDF data received:", base64Data);
        if (props.Toast) {
          props.Toast.error("Failed to generate prescription PDF");
        }
      }
    } catch (error) {
      console.error("Error printing prescription:", error);
      if (props.Toast) {
        props.Toast.error(error.message || "Failed to print prescription");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  const handleBulkPrint = async () => {
    if (selectedPrescriptionIds.length === 0) {
      if (props.Toast) {
        props.Toast.error("Please select at least one prescription to print");
      }
      return;
    }

    try {
      if (Loader) Loader(true);
      
      const response = await EmrVisitsService.printBulkPrescribedMedications(visit_id, selectedPrescriptionIds);
      
      // Handle different response structures
      const base64Data = typeof response === 'string' ? response : (response?.data || response);
      
      if (base64Data && typeof base64Data === 'string' && base64Data.length > 0) {
        const pdfData = {
          file_content: base64Data,
          original_name: `Bulk_Prescriptions_${selectedPrescriptionIds.length}`,
          mime_type: 'application/pdf'
        };
        FileManagerService.printPdf(pdfData);
        
        if (props.Toast) {
          props.Toast.success(`Successfully printed ${selectedPrescriptionIds.length} prescription(s)`);
        }
      } else {
        console.error("Invalid PDF data received:", base64Data);
        if (props.Toast) {
          props.Toast.error("Failed to generate bulk prescription PDF");
        }
      }
    } catch (error) {
      console.error("Error printing bulk prescriptions:", error);
      if (props.Toast) {
        props.Toast.error(error.message || "Failed to print bulk prescriptions");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  const handleFormChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      // Reset edit mode and data when closing
      setIsEditMode(false);
      setViewPrescriptionData(null);
    }
  };

  const handleAddPrescription = async (prescriptionData) => {
    if (!visit_id) {
      if (props.Toast) {
        props.Toast.error("Visit ID is required");
      }
      return;
    }

    try {
      Loader(true);
      // If prescription_id exists, it's an update; otherwise, it's a new prescription
      if (prescriptionData.prescription_id) {
        await EmrVisitsService.updatePrescribedMedications(visit_id, prescriptionData.prescription_id, prescriptionData);
        if (props.Toast) {
          props.Toast.success("Prescription updated successfully");
        }
      } else {
        await EmrVisitsService.savePrescribedMedications(visit_id, prescriptionData);
        if (props.Toast) {
          props.Toast.success("Prescription added successfully");
        }
      }
      handleModalToggle();
      fetchPrescribedMedications();
    } catch (error) {
      console.error("Error saving prescription:", error);
      if (props.Toast) {
        props.Toast.error(error.message || "Failed to save prescription");
      }
    } finally {
      Loader(false);
    }
  };

  return (
    <>
      <AddPrescriptionModal
        isOpen={isModalOpen}
        onClose={handleModalToggle}
        onSubmit={handleAddPrescription}
        Toast={props.Toast}
        visitDetails={visitDetails}
        visit_id={visit_id}
        Loader={Loader}
        initialData={viewPrescriptionData}
        isEditMode={isEditMode}
      />

      <div className="w-100">
        <div className="card  cardhtmd w-100" style={{height:"calc(100vh - 200px)"}}>
          <div className="card-header p-2 sh_basecardHeader">
            <div className="d-flex w-100 justify-content-between align-items-center">
              <div>Prescribed Medications</div>
              <div className="d-flex align-items-center gap-2">
                {selectedPrescriptionIds.length > 0 && (
                  <Buttons
                    className="btn btn-secondary sh_btnsecondary mr-2"
                    label={`Bulk Print (${selectedPrescriptionIds.length})`}
                    acl={PERMISSIONS.VISITS.EDIT}
                    clickHandler={handleBulkPrint}
                  />
                )}
                <Buttons
                  className="btn btn-primary sh_btnprimary"
                  label="Add Prescription"
                  acl={PERMISSIONS.VISITS.CREATE}
                  clickHandler={handleModalToggle}
                />
              </div>
            </div>
          </div>
          <div className="card-body p-3">

            {/* Table Section */}
            <div>
              {loading && (
                <div className="text-center p-4">
                  <div>Loading...</div>
                </div>
              )}
              {!loading && <TableGrid {...tableRecords} />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrescribedMedicationsPage;
